import { Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    forLifeOf,
    getDynamicRouteLink,
    getDynamicRouterLink,
    getRouterLinkById,
    getRouterLinkUrlById,
    LifecycleAware,
    TabsConfig,
    ClaimService,
    Icons
} from '@lsq-frontend/common-ui';
import { combineLatest, Observable } from 'rxjs';
import { first, map, withLatestFrom } from 'rxjs/operators';
import {
    ADMIN_BUYERS,
    ADMIN_MANAGE_BUYER_COMPANY,
    ADMIN_MANAGE_BUYER_CONFIGURATION,
    ADMIN_MANAGE_BUYER_CONTACT,
    ADMIN_MANAGE_BUYER_LOCATION,
    ADMIN_REVIEW_BUYER,
    ADMIN_SUPPLIERS,
    atlas
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { ManageBuyerService } from './manage-buyer.service';

@LifecycleAware()
@Component({
    selector: 'manage-buyer',
    styleUrls: ['./manage-buyer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    template: `
        <div class="manage-buyer">
            <page-header
                [titleText]="(buyerAccountDetails$ | async)?.buyerName"
                [shortBottomPadding]="true"
                [backButtonText]="
                    (isImplementation$ | async)
                        ? 'Back To Buyer Implementations'
                        : visitedFromSuppliers
                        ? 'Back to Suppliers'
                        : 'Back To Buyers'
                "
                (handleBackButtonClick)="navigateBack()"
            >
                <div class="data-label align-text">
                    Account Relationship
                </div>
                <p class="header align-text accountName">
                    {{ (buyerAccountDetails$ | async)?.accountName }}
                </p>
            </page-header>
            <div class="container-fluid page-container">
                <info-box *ngIf="isImplementation$ | async">
                    <div class="info-box-flex">
                        <div>
                            Buyer Setup in Progress.
                        </div>
                    </div>
                </info-box>
            </div>
            <div class="container-fluid page-container">
                <tabs [tabsConfig]="tabsConfig$ | async"></tabs>
                <router-outlet></router-outlet>
            </div>
        </div>
    `
})
export class ManageBuyerComponent {
    public visitedFromSuppliers = false;
    public buyerAccountDetails$ = this._manageBuyerService.buyerAccountDetails$;
    public isImplementation$ = this._manageBuyerService.isImplementation$;
    public navigateToReview$: Observable<string>;

    public tabsConfig$ = combineLatest([this.buyerAccountDetails$, this.isImplementation$]).pipe(
        map(([buyer, isImplementation]) => {
            if (buyer) {
                const tabs: TabsConfig[] = [
                    {
                        label: 'Buyer Information',
                        queryParamHandling: 'preserve',
                        routerLink: getDynamicRouteLink(
                            getRouterLinkUrlById(atlas, ADMIN_MANAGE_BUYER_COMPANY),
                            {
                                buyerKey: buyer.buyerKey
                            }
                        )
                    },
                    {
                        label: 'Buyer Location',
                        queryParamHandling: 'preserve',
                        routerLink: getDynamicRouteLink(
                            getRouterLinkUrlById(atlas, ADMIN_MANAGE_BUYER_LOCATION),
                            {
                                buyerKey: buyer.buyerKey
                            }
                        )
                    },
                    {
                        label: 'Configuration',
                        queryParamHandling: 'preserve',
                        routerLink: getDynamicRouteLink(
                            getRouterLinkUrlById(atlas, ADMIN_MANAGE_BUYER_CONFIGURATION),
                            {
                                buyerKey: buyer.buyerKey
                            }
                        )
                    },
                    {
                        label: 'Contact Setup',
                        queryParamHandling: 'preserve',
                        routerLink: getDynamicRouteLink(
                            getRouterLinkUrlById(atlas, ADMIN_MANAGE_BUYER_CONTACT),
                            {
                                buyerKey: buyer.buyerKey
                            }
                        )
                    }
                ];

                if (
                    !isImplementation &&
                    this._claimService.isOnSync(DashboardClaim.AdminViewSupplier)
                ) {
                    tabs.push({
                        label: 'View Suppliers',
                        queryParamHandling: 'merge',
                        routerLink: getRouterLinkUrlById(atlas, ADMIN_SUPPLIERS),
                        queryParams: {
                            buyerName: buyer.buyerName,
                            buyerKey: buyer.buyerKey
                        },
                        icon: Icons.RightArrow
                    });
                }

                if (isImplementation) {
                    tabs.push({
                        label: 'View Summary',
                        queryParamHandling: 'preserve',
                        routerLink: getDynamicRouteLink(
                            getRouterLinkUrlById(atlas, ADMIN_REVIEW_BUYER),
                            {
                                buyerKey: buyer.buyerKey
                            }
                        )
                    });
                }
                return tabs;
            }
            return null;
        })
    );

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _manageBuyerService: ManageBuyerService,
        private _claimService: ClaimService
    ) {
        this._activatedRoute.params
            .pipe(forLifeOf(this), withLatestFrom(this.isImplementation$))
            .subscribe(([params, isImplementation]) => {
                if (params?.buyerKey) {
                    if (isImplementation) {
                        this._manageBuyerService.loadBuyerImplementationAccountDetails(
                            params?.buyerKey
                        );
                    } else {
                        this._manageBuyerService.loadBuyerAccountDetails(params?.buyerKey);
                    }
                }
            });

        this._activatedRoute.queryParams.pipe(forLifeOf(this)).subscribe((queryParams) => {
            if (!!queryParams.visitedFromSuppliers) {
                this.visitedFromSuppliers = true;
            }
        });

        this.navigateToReview$ = this._manageBuyerService.buyerKey$.pipe(
            map((buyerKey) =>
                this._router
                    .createUrlTree(
                        getDynamicRouterLink(
                            getRouterLinkById(atlas, ADMIN_REVIEW_BUYER, {
                                noStandaloneLeadingSlash: true
                            }),
                            { buyerKey }
                        )
                    )
                    .toString()
            )
        );
    }

    public async navigateBack(): Promise<void> {
        if (this.visitedFromSuppliers) {
            this._router.navigate(getRouterLinkById(atlas, ADMIN_SUPPLIERS));
        } else {
            const isImplementation = await this.isImplementation$.pipe(first()).toPromise();
            const urlTree = this._router.createUrlTree(
                getRouterLinkById(atlas, ADMIN_BUYERS, {
                    noStandaloneLeadingSlash: true
                }),
                {
                    queryParams: {
                        showImplementations: isImplementation
                    }
                }
            );

            this._router.navigateByUrl(urlTree);
        }
    }
}
